import { useState } from "react";
import {
  Button,
  Checkbox,
  Modal,
  Select,
  type SelectOption,
  Textarea,
  Toast,
} from "@legal-500/legal500-design-system";
import { useMutation } from "@/hooks/useApi";
import {
  CS_CANCEL_DELETION_REASONS,
  CS_DELETE_REASONS,
  CS_PASSWORD_RESET_REASONS,
  type CsActionInput,
  type CsActionReason,
  type CsCancelDeletionResult,
  type CsCancelDeletionStatus,
  type CsDeleteResult,
  type CsPasswordResetResult,
  customerSupportUsersService,
} from "@/api/customerSupportUsers.service";
import { formatDateTimeShort } from "@/utils/dateFormatter";
import { CS_REASON_LABEL } from "./csLabels";
import styles from "./CustomerSupport.module.css";

type ActionModalProps = {
  action: "reset" | "delete" | "cancel-deletion";
  userId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  /**
   * Re-reads the profile and audit log without closing the modal or raising a success Toast —
   * for an outcome that leaves the panel behind the modal showing something untrue. Required,
   * like `onSuccess`: reaching one of those outcomes proves the rendered profile is stale, so a
   * consumer that omitted this would leave the panel contradicting the notice.
   */
  onStateRefresh: () => void;
};

/**
 * Each action takes its own reason enum, so only the chosen action's reasons are offered: a
 * reason borrowed from another action is both a 400 from its endpoint and, were it accepted, a
 * nonsense audit row (a password reset filed as an erasure).
 */
const ACTION_REASONS: Record<ActionModalProps["action"], readonly CsActionReason[]> = {
  reset: CS_PASSWORD_RESET_REASONS,
  delete: CS_DELETE_REASONS,
  "cancel-deletion": CS_CANCEL_DELETION_REASONS,
};

const MODAL_TITLE: Record<ActionModalProps["action"], string> = {
  reset: "Send password reset",
  delete: "Delete account + data",
  "cancel-deletion": "Cancel scheduled deletion",
};

const CONFIRM_LABEL: Record<ActionModalProps["action"], { idle: string; busy: string }> = {
  reset: { idle: "Send password reset", busy: "Sending…" },
  delete: { idle: "Delete account + data", busy: "Scheduling…" },
  "cancel-deletion": { idle: "Cancel scheduled deletion", busy: "Cancelling…" },
};

/**
 * The two non-"cancelled" statuses are successful 200s that reversed nothing, so each states the
 * account's real state — presenting either as a completed cancellation would mislead the agent.
 */
const CANCEL_DELETION_NOTICE: Record<Exclude<CsCancelDeletionStatus, "cancelled">, string> = {
  already_deleted:
    "Nothing was cancelled: this account has already been deleted, so there is nothing left to restore.",
  not_pending: "Nothing was cancelled: this account has no scheduled deletion.",
};

export default function ActionModal({
  action,
  userId,
  onClose,
  onSuccess,
  onStateRefresh,
}: ActionModalProps) {
  const [selectedReason, setSelectedReason] = useState<CsActionReason | "">("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  // An action can succeed without changing anything (see CANCEL_DELETION_NOTICE); that outcome is
  // neither the parent's success Toast nor the mutation's error, so it needs its own surface.
  const [notice, setNotice] = useState<string | null>(null);

  const { mutate, loading, error, reset } = useMutation<
    CsPasswordResetResult | CsDeleteResult | CsCancelDeletionResult,
    [CsActionInput]
  >(input =>
    action === "reset"
      ? customerSupportUsersService.sendPasswordReset(userId, input)
      : action === "delete"
        ? customerSupportUsersService.deleteUser(userId, input)
        : customerSupportUsersService.cancelDeletion(userId, input),
  );

  const allowedReasons = ACTION_REASONS[action];
  const reasonOptions: SelectOption[] = [
    { value: "", label: "Select a reason…", disabled: true },
    ...allowedReasons.map(value => ({ value, label: CS_REASON_LABEL[value] })),
  ];
  // UserProfile swaps the `action` prop on the mounted modal rather than remounting it, and Modal
  // traps no focus, so a keyboard agent can reach the buttons behind it and change action with a
  // reason already picked. A reason the new action does not accept counts as unselected, which
  // re-gates Confirm until one it does accept is chosen.
  const reason: CsActionReason | "" =
    selectedReason === "" || allowedReasons.includes(selectedReason) ? selectedReason : "";

  // Confirm is gated on: a reason chosen, AND (if "other") non-empty free-text,
  // AND (if deleting) the confirmation checkbox ticked. Also blocked while busy, and blocked
  // for good once a notice stands: the backend has already reported that this account cannot
  // reach the action's outcome, so a resubmit could only write a second CS audit row for a
  // provable no-op. The reason controls stay live; only the pointless submit is closed off.
  const reasonChosen = reason !== "";
  const detailProvided = reason !== "other" || reasonDetail.trim() !== "";
  const deleteConfirmed = action !== "delete" || confirmed;
  const canConfirm =
    reasonChosen && detailProvided && deleteConfirmed && !loading && notice === null;

  const handleConfirm = async () => {
    if (!canConfirm || !reason) {
      return;
    }

    setNotice(null);

    const input: CsActionInput = {
      reason,
      reasonDetail: reason === "other" ? reasonDetail.trim() : undefined,
    };

    // `useMutation.mutate` both stores the failure in its own `error` state (which the Toast
    // at the bottom of this modal renders) and rethrows it. Swallowing that rethrow in a bare
    // `catch {}` hid the failure from anyone reading the code; letting it escape an event
    // handler would surface as an unhandled rejection instead of the Toast. So the rejection
    // is mapped to an explicit outcome and branched on — the mutation's `error` stays the
    // single error surface, and nothing is discarded silently.
    const outcome = await mutate(input).then(
      result => ({ succeeded: true as const, result }),
      () => ({ succeeded: false as const }),
    );

    if (!outcome.succeeded) {
      // Failure keeps the modal open so the agent can retry; `error` drives the Toast.
      return;
    }

    if (action === "cancel-deletion") {
      const { status } = outcome.result as CsCancelDeletionResult;
      if (status !== "cancelled") {
        // The modal stays open on the notice because closing it behind a success Toast would
        // report a reversal that never happened. The panel behind it still refreshes: this
        // action is only offered against a pending, un-anonymised deletion, so reaching either
        // of these statuses proves the rendered profile — and the audit log, which the backend
        // appended to on these branches too — no longer describes the account.
        setNotice(CANCEL_DELETION_NOTICE[status]);
        onStateRefresh();
        return;
      }
      onSuccess("Scheduled deletion cancelled — the account is restored to verified.");
      return;
    }

    const message =
      action === "delete"
        ? `Deletion scheduled for ${formatDateTimeShort((outcome.result as CsDeleteResult).deletionScheduledAt)}.`
        : "Password reset email sent.";
    // onSuccess closes the modal itself; calling onClose again here would set state on a
    // parent that has already unmounted this component.
    onSuccess(message);
  };

  return (
    <Modal isOpen onClose={onClose} title={MODAL_TITLE[action]} size="small">
      <div className={styles.modalForm}>
        {action === "delete" && (
          <p className={styles.modalBody}>
            This <strong>schedules</strong> permanent deletion of the account and its data after a
            30-day grace period. It is <strong>not immediate</strong> — the account can be recovered
            until the grace period ends.
          </p>
        )}

        {action === "cancel-deletion" && (
          <p className={styles.modalBody}>
            This <strong>stops the scheduled purge</strong> and restores the account to{" "}
            <strong>verified</strong>. It works whether the deletion was started by staff or by the
            user themself.
          </p>
        )}

        {notice && (
          <p className={styles.modalNotice} role="status">
            {notice}
          </p>
        )}

        <Select
          label="Reason"
          options={reasonOptions}
          value={reason}
          onChange={event => setSelectedReason(event.target.value as CsActionReason | "")}
          disabled={loading}
          fullWidth
        />

        {reason === "other" && (
          // The only free text an agent authors here, and it lands on the append-only CS audit
          // row that deliberately outlives the account's erasure — so the caution belongs on the
          // field for every action this modal serves, not only delete. `maxLength` mirrors the
          // cap all three endpoints enforce, so an over-long entry is stopped at the keystroke
          // rather than as a 400 after the agent has typed it.
          <Textarea
            label="Reason detail"
            helperText="Do not include the user's personal information — this entry is kept on the audit log after the account is deleted."
            maxLength={1000}
            value={reasonDetail}
            onChange={event => setReasonDetail(event.target.value)}
            disabled={loading}
            fullWidth
          />
        )}

        {action === "delete" && (
          <Checkbox
            label="I understand this schedules permanent deletion after the 30-day grace period."
            checked={confirmed}
            onChange={event => setConfirmed(event.target.checked)}
            disabled={loading}
          />
        )}

        <div className={styles.modalFooter}>
          {/* "Close", not "Cancel": beside a "Cancel scheduled deletion" confirm, a "Cancel"
              dismiss reads as a second way to perform the action rather than to abandon it. */}
          <Button variant="outlined" color="default" onClick={onClose} disabled={loading}>
            Close
          </Button>
          <Button
            variant="contained"
            color={action === "delete" ? "error" : "primary"}
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {loading ? CONFIRM_LABEL[action].busy : CONFIRM_LABEL[action].idle}
          </Button>
        </div>
      </div>

      {error && <Toast message={error.message} type="error" duration={5000} onClose={reset} />}
    </Modal>
  );
}
